import { Button, Drawer, Empty, Segmented } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import GroupHeader from '../common/GroupHeader';
import InboxRow from './InboxRow';
import { useOptionalNotifications } from '../../contexts/NotificationsContext';
import { formatCount } from '../../utils/counts';
import { isUnresolvedAlert, needsAttention } from '../../utils/notificationCopy';
import { normalizeId } from '../../utils/ids';
import { DataState, Skeleton, deriveDataState } from '../loading';
import useWindowClass from '../../hooks/useWindowClass';

/**
 * The notification inbox behind the bell.
 *
 * A row is a prompt, not just a record: the `notify` action attaches CTAs, and
 * a `create-item` CTA is the whole point of the expected-item feature ("Suzy
 * has no med administration today" → "Log it" → a PREFILLED new-item form).
 * The prefill payload itself never rides in the URL — it lives on the
 * notification row, and the link carries only `?notification=<id>` so the form
 * can pull it from the inbox it already has (rich-text prefills would blow past
 * URL limits, and URLs leak into logs). `InboxRow` renders all of that.
 *
 * TWO buckets, not three. A notification is either awaiting something from you
 * or it is history; a third tab would invent a state the model has not got.
 * Open is `{state: {$ne: 'resolved'}}` — the server's own query, said again
 * here — so the rows written before this feature, which have no state at all,
 * stay exactly where their readers expect them.
 *
 * An OPEN alert outranks an unread one: open rows are pinned into "Needs
 * attention" above the chronological list, and they do not leave it when read.
 * Reading a thing is not doing it.
 */

// Above this many rows the day headers earn their space; below it they are
// furniture over a list you can already see the whole of. "Needs attention"
// is not subject to the threshold — it is what makes those rows a group
// rather than three rows that happen to be at the top.
const DAY_HEADER_THRESHOLD = 8;

const FILTERS = [
	{ value: 'open', label: 'Open' },
	{ value: 'all', label: 'All' },
];

const DAY_LABELS = { today: 'Today', yesterday: 'Yesterday', earlier: 'Earlier' };

const dayBucket = ( row, now ) => {
	const created = dayjs( row?.createdAt );
	if ( ! created.isValid() ) return 'earlier';
	if ( created.isSame( now, 'day' ) ) return 'today';
	if ( created.isSame( now.subtract( 1, 'day' ), 'day' ) ) return 'yesterday';
	return 'earlier';
};

/**
 * The list, in sections: what needs you first, then everything else by the day
 * it arrived. Exported for the tests, because the ordering IS the feature — a
 * resolved row demoting out of "Needs attention" on the next fetch is the one
 * behaviour the whole state model is for.
 */
export function groupInbox( rows, { now = dayjs() } = {} ) {
	const pinned = rows.filter( needsAttention );
	const rest = rows.filter( row => ! needsAttention( row ) );

	const days = new Map( [ [ 'today', [] ], [ 'yesterday', [] ], [ 'earlier', [] ] ] );
	rest.forEach( row => { days.get( dayBucket( row, now ) ).push( row ); } );

	return [
		pinned.length ? { key: 'attention', label: 'Needs attention', rows: pinned, always: true } : null,
		...[ ...days.entries() ]
			.filter( ( [ , group ] ) => group.length )
			.map( ( [ key, group ] ) => ( { key, label: DAY_LABELS[ key ], rows: group, always: false } ) ),
	].filter( Boolean );
}

export default function NotificationsDrawer( { open, onClose } ) {

	const navigate = useNavigate();
	// The shell's own class, so the inbox is full width exactly where the tab bar is.
	const { compact: isMobile } = useWindowClass();

	// Optional on purpose: the header renders without the provider in tests and
	// on the public landing pages. No provider simply means an empty inbox.
	const inbox = useOptionalNotifications();
	const all = inbox?.notifications || [];
	const unreadCount = inbox?.unreadCount || 0;
	const openCount = inbox?.openCount || 0;
	const filter = inbox?.filter || 'open';

	// The server filters too; this is the client saying the same sentence, so a
	// build talking to an API that has not grown `?state=` yet still shows the
	// right tab rather than everything under "Open".
	const notifications = filter === 'open' ? all.filter( isUnresolvedAlert ) : all;
	const groups = groupInbox( notifications );
	const showDayHeaders = notifications.length > DAY_HEADER_THRESHOLD;

	const manage = () => {
		navigate( '/account/notifications' );
		onClose?.();
	};

	const footer = (
		<div className="inbox__foot">
			<span className="inbox__showing">
				{filter === 'open'
					? ( formatCount( openCount || notifications.length, 'open notification' ) || 'Nothing open' )
					: ( formatCount( notifications.length, 'notification' ) || 'Nothing here' )}
			</span>
			{inbox?.hasMore ? (
				<Button size="small" loading={inbox?.loadingMore} onClick={() => inbox?.loadMore?.()}>
					Load older
				</Button>
			) : null}
			<Button type="link" size="small" onClick={manage}>Manage notifications</Button>
		</div>
	);

	return (
		<Drawer
			className="inbox"
			title={(
				<span className="inbox__head">
					Inbox
					{unreadCount ? (
						<span className="inbox__unread" aria-label={`${ unreadCount } unread`}>
							{unreadCount > 99 ? '99+' : unreadCount}
						</span>
					) : null}
				</span>
			)}
			placement='right'
			open={open}
			onClose={onClose}
			width={isMobile ? '100%' : 420}
			footer={footer}
			extra={(
				<Button
					type='link'
					disabled={! unreadCount}
					onClick={() => inbox?.markAllRead?.()}
				>
					Mark all read
				</Button>
			)}
		>
			<div className="inbox__toolbar">
				<Segmented
					size="small"
					value={filter}
					options={FILTERS}
					onChange={value => inbox?.setFilter?.( value )}
					aria-label="Which notifications to show"
				/>
			</div>

			{/* A drawer that reads on open: a skeleton until the first answer, a
			    failure with Retry (never "all caught up" over a failed read), and
			    the empty line only once a read has said there is nothing. */}
			<DataState
				className="inbox__state"
				state={deriveDataState( {
					loading: Boolean( inbox?.loading ),
					error: inbox?.loadFailed ? true : null,
					hasData: Boolean( all.length ),
					isEmpty: ! notifications.length,
				} )}
				hasData={Boolean( all.length )}
				isEmpty={! notifications.length}
				skeleton={<Skeleton variant="list-row" count={5} />}
				empty={(
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={filter === 'open' ? "You're all caught up." : 'Nothing here yet.'}
					/>
				)}
				onRetry={() => inbox?.refresh?.()}
				label="notifications"
			>
			{groups.map( group => (
				<section key={group.key} className={`inbox__group inbox__group--${ group.key }`}>
					{group.always || showDayHeaders ? (
						<GroupHeader label={group.label} count={group.rows.length} sticky />
					) : null}
					<ul className="inbox__rows">
						{group.rows.map( row => (
							<InboxRow
								key={normalizeId( row?._id )}
								notification={row}
								currentUserId={inbox?.currentUserId}
								onMarkRead={id => inbox?.markRead?.( id )}
								onMarkUnread={id => inbox?.markUnread?.( id )}
								onDismiss={id => inbox?.dismiss?.( id )}
								onRespond={( id, key, options ) => inbox?.respond?.( id, key, options )}
								onClose={onClose}
							/>
						) )}
					</ul>
				</section>
			) )}
			</DataState>
		</Drawer>
	);
}

// The CTA contract lives with the row that renders it; the drawer keeps
// exporting it because that is where every caller has always imported it from.
export { ctaTarget } from './InboxRow';
